import { isBoutiqueSide } from "@/features/invoices/invoice-utils";
import { useMemo } from "react";
import * as Yup from "yup";
import { Company } from "@/features/types";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useYupHelper from "@/features/ui/form/use-yup-helper";
import PriceUtils from "@/features/ui/utils/price-utils";
import { BankRemittanceReceiptFormFields } from "@/features/invoices/bank-remittance-receipts/upload-bank-remittance-receipt-page";

export default function useBankRemittanceReceiptFormValidationSchema() {
  const { t, tTitle } = useI18nHelper();
  const { withMessage } = useYupHelper({
    ns: "bank_remittance_receipts",
    prefix: "update_bank_remittance_receipt_form",
  });

  return useMemo(() => {
    return Yup.object<BankRemittanceReceiptFormFields>({
      name: Yup.string(),
      season: Yup.string().required(withMessage.required),
      orderedBy: Yup.mixed<Company>().required(withMessage.required),
      // issuedBy: Yup.mixed<Company>().required(withMessage.required),
      issuedThrough: Yup.mixed<Company>().required(withMessage.required),
      issuedOn: Yup.date().required(withMessage.required),
      payments: Yup.array()
        .of(
          Yup.object({
            id: Yup.number(),
          })
        )
        .required(withMessage.required)
        .min(1, withMessage.min),
      amount: Yup.object({
        value: Yup.number().required(withMessage.required),
        currency: Yup.string().required(withMessage.required),
      }).test(
        "amount_less_than_unremitted_amount",
        t(
          "bank_remittance_receipts:upload_bank_remittance_receipts_form.messages.error.amount_less_than_unremitted_amount"
        ),
        (value, context) => {
          const { payments } =
            context.parent as BankRemittanceReceiptFormFields;
          if (payments && payments.length > 0) {
            return (
              value.value <=
              PriceUtils.sum(
                payments.map((p) => p.amount),
                "EUR"
              ).value!
            );
          }
        }
      ),
      bankRemittanceReceiptDetailList: Yup.array()
        .of(
          Yup.object({
            payment: Yup.object({
              id: Yup.number().required(withMessage.required),
              name: Yup.string().required(withMessage.required),
            }).required(withMessage.required),
            amount: Yup.object({
              value: Yup.number()
                .label("remitted_amount")
                .required(withMessage.required),
              currency: Yup.string().required(withMessage.required),
            }),
          })
        )
        .required(withMessage.required)
        .min(1, withMessage.min)
        .test(
          "amount_less_than_unremitted_amount",
          t(
            "bank_remittance_receipts:upload_bank_remittance_receipts_form.messages.error.amount_less_than_unremitted_amount"
          ),
          (value, context) => {
            const { payments } =
              context.parent as BankRemittanceReceiptFormFields;
            if (payments && payments.length > 0) {
              return value.some((detail) => {
                const payment = payments.find(
                  (p) => p.id === detail.payment.id
                );
                return (
                  payment &&
                  detail.amount.value <= payment.unremittedAmount.value
                );
              });
            }
          }
        ),

      file: Yup.mixed<File>().test(
        "file_required_for_overseas_product_payment",
        t(
          "bank_remittance_receipts:upload_bank_remittance_receipts_form.messages.error.file_required_for_overseas_product_payment"
        ),
        (value, context) => {
          const { receiver, amount } =
            context.parent as BankRemittanceReceiptFormFields;
          // 상품 대금(PRODUCT)의 해외 송금(수신=BOUTIQUE)일 때만 파일 첨부 필수.
          // 원화 송금(국내 부티크에 바이어가 직접 보내는 경우 등)은 국내 송금이라 받지 않는다.
          const isOverseasProductPayment =
            context.options.context?.paymentType === "PRODUCT" &&
            isBoutiqueSide(receiver?.type) &&
            amount?.currency !== "KRW";
          if (isOverseasProductPayment) {
            return !!value;
          }
          return true;
        }
      ),
      sender: Yup.mixed<Company>()
        .label("sender")
        .required(withMessage.required),
      receiver: Yup.mixed<Company>()
        .label("receiver")
        .required(withMessage.required),
    });
  }, [t, withMessage]);
}
